import { Box, Chip, Table, Card, TextField, Icon, Skeleton, withStyles, MaterialSymbol } from 'vopay';
import { Button, DropdownMenu, Title } from 'UI';
import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import useFetchUsers from '@/hooks/useApi/useFetchUsers';
import useDeleteUser from '@/hooks/useApi/useDeleteUser';
import { useTranslation } from 'react-i18next';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useDialog } from '@/providers/Dialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import RolesSummaryCard from './UserRoles/RolesSummaryCard';
import useComputePartnerRoleCounts from './UserRoles/useComputePartnerRoleCounts';
import useUserRoleLabels from '@/hooks/useUserRoleLabels';
import ManageRoleDialog from './ManageRole/ManageRoleDialog';
import ChangePasswordDialog from './ChangePassword/ChangePasswordDialog';
import CreateUserDrawer from './CreateUser/CreateUserDrawer';
import styles from './UsersList.module.scss';

const cn = withStyles(styles);

type UsersListProps = {
  /**
   * The users of a partner account the logged-in partner created, instead of its own: sent as
   * `PartnerAccountID` on every user route, with the logged-in partner's own
   * session. Omitted, the page manages the logged-in partner's users as before.
   */
  partnerAccountID?: string;
  /** Replaces the page title when the page is embedded under another heading. */
  title?: string;
  /** A page title by default; lower when embedded in a card. */
  titleLevel?: ComponentProps<typeof Title>['level'];
};

/**
 * The partner portal's User Management: the logged-in partner's portal users at /users, and — given
 * a `partnerAccountID` — one of its partner accounts' users, embedded on that partner's page. An
 * Admin adds users, changes their role and password and removes them; a Viewer only
 * looks.
 */
export default function UsersList({ partnerAccountID, title, titleLevel = '1' }: UsersListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateUserDrawerOpen, setIsCreateUserDrawerOpen] = useState(false);
  const { deleteUser } = useDeleteUser();
  const roleLabels = useUserRoleLabels();
  const { users, fetchingUsers, fetchUsers } = useFetchUsers();
  const { t } = useTranslation('translations', { keyPrefix: 'Users.List' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t: tDeleteUser } = useTranslation('translations', { keyPrefix: 'Users.List.DeleteUser' });
  const dialog = useDialog();
  const toast = useToast();
  const alertDialog = useAlertDialog();

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const refetchUsers = useCallback(
    () =>
      fetchUsers({ PartnerAccountID: partnerAccountID }).catch((error) => {
        // The hook has logged the cause.
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        toast.neutral(t('loadError', { defaultValue: 'Could not load the users: {{errorMessage}}', errorMessage }));
      }),
    // toast is deliberately left out: the provider recreates its value every render.
    [fetchUsers, partnerAccountID, t],
  );

  useEffect(() => {
    refetchUsers();
  }, [refetchUsers]);

  const filteredUsers = useMemo(
    () => users.filter((user) => searchTerm === '' || user.Username.toLowerCase().includes(searchTerm.toLowerCase())),
    [users, searchTerm],
  );

  const roleCounts = useComputePartnerRoleCounts(users);

  const handleManageRoleDialog = async (username: string, currentRole: string) => {
    const result = await dialog.open<boolean>(({ onConfirm, onCancel }) => (
      <ManageRoleDialog
        onConfirm={onConfirm}
        onCancel={onCancel}
        emailAddress={username}
        currentRole={currentRole}
        partnerAccountID={partnerAccountID}
      />
    ));

    if (result === null) return;

    if (result) {
      toast.success(t('ManageRoleDialog.success', { defaultValue: 'The role was updated successfully.' }));
      refetchUsers();
    } else {
      toast.neutral(t('ManageRoleDialog.error', { defaultValue: 'Unable to update the role.' }));
    }
  };

  const handleChangePasswordDialog = async (username: string) => {
    const result = await dialog.open<boolean>(({ onConfirm, onCancel }) => (
      <ChangePasswordDialog
        onConfirm={onConfirm}
        onCancel={onCancel}
        emailAddress={username}
        partnerAccountID={partnerAccountID}
      />
    ));

    if (result === null) return;

    if (result) {
      toast.success(t('ChangePasswordDialog.success', { defaultValue: 'The password was updated successfully.' }));
    } else {
      toast.neutral(t('ChangePasswordDialog.error', { defaultValue: 'Unable to update the password.' }));
    }
  };

  const handleDeleteUser = async (username: string) => {
    const result = await alertDialog.caution(
      tDeleteUser('Confirmation.title', { defaultValue: 'Are you sure you want to delete the user?' }),
      tDeleteUser('Confirmation.subTitle', { defaultValue: 'This action cannot be undone.' }),
      {
        abortLabel: tDeleteUser('Confirmation.cancelLabel', { defaultValue: 'No' }),
        confirmLabel: tDeleteUser('Confirmation.confirmLabel', { defaultValue: 'Yes' }),
      },
    );

    if (result !== 'confirm') return;

    try {
      await deleteUser({ EmailAddress: username, PartnerAccountID: partnerAccountID });
      toast.success(tDeleteUser('Success.title', { defaultValue: 'Success! The user was successfully deleted.' }));
      refetchUsers();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      alertDialog.error(
        tDeleteUser('Error.title', { defaultValue: 'Error! The user was not deleted.' }),
        tDeleteUser('Error.subTitle', { defaultValue: '{{errorMessage}}', errorMessage }),
      );
    }
  };

  return (
    <div>
      <Box mb="md" jc="sb" ai="center">
        <Title level={titleLevel}>{title ?? t('pageTitle', { defaultValue: 'User Management' })}</Title>

        <div className={cn('searchArea')}>
          <TextField
            key="UserSearchKeyword"
            name="UserSearchKeyword"
            placeholder={t('Search.placeholder', { defaultValue: 'Search user' })}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            variant="outlineShadow"
            elementLeft={
              <div className={cn('searchIcon')}>
                <Icon icon="search" width="17px" height="17px" color="none" />
              </div>
            }
          />

          <Button
            id="addUserButton"
            colorScheme="primary"
            size="small"
            iconLeft="add"
            disabled={isRoleViewOnly}
            onClick={() => setIsCreateUserDrawerOpen(true)}
          >
            {t('addUserButton', { defaultValue: 'Add User' })}
          </Button>
        </div>
      </Box>

      <RolesSummaryCard roles={roleCounts} fetching={fetchingUsers} />

      <Card className={cn('tableCard')}>
        {fetchingUsers ? (
          <Skeleton height={320} data-testid="usersListLoading" />
        ) : (
          <Table
            id="users-list"
            version="2"
            zebra
            columns={[
              {
                id: 'Username',
                header: t('Table.username'),
                align: 'left',
                field: 'Username',
              },
              {
                id: 'DateAdded',
                header: t('Table.dateAdded'),
                align: 'left',
                field: 'DateAdded',
              },
              {
                id: 'UserType',
                header: t('Table.role', { defaultValue: 'Role' }),
                align: 'left',
                template: ({ data }) => <Chip label={roleLabels[data.UserType] ?? data.UserType} />,
              },
              {
                id: 'view',
                align: 'right',
                header: '',
                template: ({ data }) => (
                  <Box jc="fe" gap="sm">
                    <DropdownMenu>
                      <DropdownMenu.Trigger asChild>
                        <Button
                          size="small"
                          colorScheme="secondary"
                          iconRight="keyboard_arrow_down"
                          disabled={isRoleViewOnly}
                        >
                          {tGlobal('actions')}
                        </Button>
                      </DropdownMenu.Trigger>

                      <DropdownMenu.Content align="end">
                        {/* TODO: label is singular ("Manage Role") and the target dialog is a
                          single-role Select, because partner users hold exactly one UserType.
                          Account Portal's equivalent is "Manage Role(s)" backed by a multi-select
                          (SelectRoleDialogue, many RoleIDs per user). When ID-13192 shares this
                          component, this needs a real branch on portal type for both the copy
                          and the dialog implementation — not just a label swap. */}
                        <DropdownMenu.Item
                          onSelect={() => handleManageRoleDialog(data.Username, data.UserType)}
                          left={<MaterialSymbol icon="groups_2" size={18} />}
                        >
                          {t('Table.manageRoleButton', { defaultValue: 'Manage Role' })}
                        </DropdownMenu.Item>

                        <DropdownMenu.Item
                          onSelect={() => handleChangePasswordDialog(data.Username)}
                          left={<MaterialSymbol icon="lock" size={18} />}
                        >
                          {t('Table.changePasswordButton')}
                        </DropdownMenu.Item>

                        <DropdownMenu.Item
                          onSelect={() => handleDeleteUser(data.Username)}
                          colorScheme="destructive"
                          left={<MaterialSymbol icon="close" size={18} color="#e8160b" />}
                        >
                          {t('Table.removeButton')}
                        </DropdownMenu.Item>
                      </DropdownMenu.Content>
                    </DropdownMenu>
                  </Box>
                ),
              },
            ]}
            rows={filteredUsers.map((user) => ({
              id: user.Username,
              data: user,
            }))}
          />
        )}
      </Card>

      <CreateUserDrawer
        isOpen={isCreateUserDrawerOpen}
        existingUsernames={users.map((user) => user.Username)}
        partnerAccountID={partnerAccountID}
        onClose={() => setIsCreateUserDrawerOpen(false)}
        onUserCreated={refetchUsers}
      />
    </div>
  );
}
